
import { useQuery } from '@tanstack/react-query';
import { NotificationService } from '@/utils/notificationService';
import { useCurrentUserFamily } from './useCurrentUserFamily';

/**
 * Hook to monitor notification queue status and health
 * @param isAdmin - If true, fetches admin-wide data instead of family-specific data
 */
export const useNotificationQueue = (isAdmin: boolean = false) => {
  const { familyId } = useCurrentUserFamily();

  const {
    data: queueStats,
    isLoading: isLoadingStats,
    refetch: refetchStats
  } = useQuery({
    queryKey: isAdmin ? ['admin-notification-queue-stats'] : ['notification-queue-stats'],
    queryFn: () => isAdmin ? NotificationService.getAdminQueueStats() : NotificationService.getQueueStats(),
    refetchInterval: isAdmin ? 10000 : 30000, // Admin refreshes every 10 seconds
    staleTime: isAdmin ? 5000 : 20000, // Admin data is stale after 5 seconds
  });

  const {
    data: notificationHistory,
    isLoading: isLoadingHistory
  } = useQuery({
    queryKey: isAdmin ? ['admin-notification-history'] : ['notification-history', familyId],
    queryFn: () => {
      if (isAdmin) {
        return NotificationService.getAdminNotificationHistory();
      }
      return familyId ? NotificationService.getFamilyNotificationHistory(familyId) : Promise.resolve([]);
    },
    enabled: isAdmin || !!familyId,
    staleTime: 60000, // Consider history stale after 1 minute
  });

  const {
    data: pushConfig,
    isLoading: isLoadingConfig,
    refetch: refetchConfig
  } = useQuery({
    queryKey: isAdmin ? ['admin-push-configuration'] : ['push-configuration'],
    queryFn: () => isAdmin ? NotificationService.getAdminPushConfiguration() : NotificationService.checkPushConfiguration(),
    staleTime: isAdmin ? 30000 : 300000, // Admin config is stale after 30 seconds
  });

  const triggerQueueProcessing = async () => {
    const success = await NotificationService.triggerQueueProcessing();
    if (success) {
      // Refetch stats after triggering processing
      setTimeout(() => refetchStats(), 2000);
    }
    return success;
  };

  return {
    queueStats,
    notificationHistory: notificationHistory || [],
    pushConfig,
    isLoading: isLoadingStats || isLoadingHistory || isLoadingConfig,
    triggerQueueProcessing,
    refetchStats,
    refetchConfig
  };
};
